import {
  ChangeEventHandler,
  FocusEventHandler,
  KeyboardEventHandler,
  MouseEventHandler,
  forwardRef,
  useEffect,
  useRef,
} from "react";
import styles from "./TextInput.module.scss";
import { useMergeRefs } from "@floating-ui/react";
import InputWrapper, { InputWrapperProps } from "../InputWrapper/InputWrapper";
import classNames from "classnames";

interface InputAutosizeProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  value?: string;
}

const InputAutosize = forwardRef<HTMLInputElement, InputAutosizeProps>(
  function InputAutosize({ value, className, ...props }, ref) {
    return (
      <div className={styles.autosize}>
        <span className={styles.autosizeSpan}>
          {!value && (props.placeholder ?? "\u00A0")}
          {value?.replace(/ /g, "\u00A0")}
        </span>
        <input
          {...props}
          type="text"
          value={value}
          className={classNames(styles.input, styles.autosizeInput, className)}
          ref={ref}
        />
      </div>
    );
  },
);

type TextInputProps = {
  value?: string;
  placeholder?: string;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  onFocus?: FocusEventHandler<HTMLInputElement>;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
  onKeyUp?: KeyboardEventHandler<HTMLInputElement>;
  disabled?: boolean;
  defaultValue?: string;
  onClick?: MouseEventHandler<HTMLInputElement>;
  autosize?: boolean;
} & Omit<InputWrapperProps, "children" | "inputRef">;

const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  function TextInput(
    {
      className,
      leftSection,
      rightSection,
      onChange,
      onClick,
      onBlur,
      onKeyDown,
      onKeyUp,
      onFocus,
      autofocus,
      value,
      placeholder,
      disabled,
      size,
      unstyled,
      defaultValue,
      error,
      clearable,
      onClear,
      autosize,
      loading,
      loadingMessage,
    },
    ref,
  ) {
    const internalRef = useRef<HTMLInputElement>(null);
    const refs = useMergeRefs([ref, internalRef]);

    useEffect(() => {
      if (autofocus) {
        setTimeout(() => {
          internalRef.current?.focus();
        });
      }
    }, [autofocus, internalRef]);

    return (
      <InputWrapper
        className={className}
        leftSection={leftSection}
        rightSection={rightSection}
        disabled={disabled}
        size={size}
        unstyled={unstyled}
        error={error}
        clearable={clearable}
        onClear={onClear}
        loading={loading}
        loadingMessage={loadingMessage}
      >
        {autosize ? (
          <InputAutosize
            onClick={onClick}
            ref={refs}
            type="text"
            onChange={onChange}
            onBlur={onBlur}
            onKeyDown={onKeyDown}
            onKeyUp={onKeyUp}
            onFocus={onFocus}
            value={loading ? "" : value}
            placeholder={placeholder}
            disabled={disabled}
            defaultValue={defaultValue}
          />
        ) : (
          <input
            onClick={onClick}
            ref={refs}
            className={styles.input}
            type="text"
            onChange={onChange}
            onBlur={onBlur}
            onKeyDown={onKeyDown}
            onKeyUp={onKeyUp}
            onFocus={onFocus}
            value={value}
            placeholder={placeholder}
            disabled={disabled}
            defaultValue={defaultValue}
          />
        )}
      </InputWrapper>
    );
  },
);

export default TextInput;
